import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { Footer, Header } from './headerFooter';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useNavigate, Link } from 'react-router-dom';
import { EIDIKI_TABLE_OPTIONS, PREMIUM_ACCESS_UNTIL_LABEL } from './constants';
import { getHost } from './config';
import PayPalDonateSdkButton from './PayPalDonateSdkButton';
import { trackEvent } from './analytics';
import FilterSidebar from './FilterSidebar';
import { initialSidebarOpen, collapseFilterSidebarOnMobile } from './filterSidebarUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import './custom.scss';

// Fixed year for API (specialty-types, places). Not shown to user.
const FIXED_YEAR_RANGE = '2025_2026';
const MAX_LOCATIONS = 10;
const EIDIKI_AGOGI_DEFAULT_YES_SPECIALTIES = [
    'ΠΕ21', 'ΠΕ22', 'ΠΕ23', 'ΠΕ25', 'ΠΕ28', 'ΠΕ29', 'ΠΕ30', 'ΔΕ01', 'ΠΕ87.02'
];

// School year: 10/09 → 30/06. { month: daysInMonth }
const SCHOOL_YEAR_MONTHS = [
    { month: 9, days: 30, label: 'Σεπτέμβριος', genitiveLabel: 'Σεπτεμβρίου' },
    { month: 10, days: 31, label: 'Οκτώβριος', genitiveLabel: 'Οκτωβρίου' },
    { month: 11, days: 30, label: 'Νοέμβριος', genitiveLabel: 'Νοεμβρίου' },
    { month: 12, days: 31, label: 'Δεκέμβριος', genitiveLabel: 'Δεκεμβρίου' },
    { month: 1, days: 31, label: 'Ιανουάριος', genitiveLabel: 'Ιανουαρίου' },
    { month: 2, days: 28, label: 'Φεβρουάριος', genitiveLabel: 'Φεβρουαρίου' },
    { month: 3, days: 31, label: 'Μάρτιος', genitiveLabel: 'Μαρτίου' },
    { month: 4, days: 30, label: 'Απρίλιος', genitiveLabel: 'Απριλίου' },
    { month: 5, days: 31, label: 'Μάιος', genitiveLabel: 'Μαΐου' },
    { month: 6, days: 30, label: 'Ιούνιος', genitiveLabel: 'Ιουνίου' },
];

function hireDateCalendarYear(month) {
    const currentYear = new Date().getFullYear();
    return month >= 9 ? currentYear : currentYear + 1;
}

function monthDayToHireDateStr(month, day) {
    const year = hireDateCalendarYear(month);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatHireDateTitleSuffix(month, day) {
    const entry = SCHOOL_YEAR_MONTHS.find((m) => m.month === month);
    const year = hireDateCalendarYear(month);
    const monthGenitive = entry?.genitiveLabel ?? '';
    return `μέχρι ${day} ${monthGenitive} ${year}`;
}

function Predict() {
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');
    const [specialty, setSpecialty] = useState('');
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [specialtyLoading, setSpecialtyLoading] = useState(false); // Loading state for specialty fetch
    const [placesOptions, setPlacesOptions] = useState([]);
    const [placesLoading, setPlacesLoading] = useState(false); // Loading state for places fetch
    const [locations, setLocations] = useState([]);
    const [userPosition, setUserPosition] = useState('');
    const [tableType, setTableType] = useState(EIDIKI_TABLE_OPTIONS[0]?.value || '');
    const [eidikiAgogi, setEidikiAgogi] = useState('false');
    const [hireMonth, setHireMonth] = useState(9);
    const [hireDay, setHireDay] = useState(10);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [remainingFreeAttempts, setRemainingFreeAttempts] = useState(null);
    const [isPremiumUser, setIsPremiumUser] = useState(null);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [ratingScaleOpen, setRatingScaleOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);

    const maxDaysForMonth = useMemo(() => {
        const entry = SCHOOL_YEAR_MONTHS.find((m) => m.month === hireMonth);
        return entry ? entry.days : 30;
    }, [hireMonth]);

    const hireDateStr = useMemo(
        () => monthDayToHireDateStr(hireMonth, hireDay),
        [hireMonth, hireDay]
    );

    const sortedProbabilities = useMemo(() => {
        if (!data?.probabilities || typeof data.probabilities !== 'object') return [];
        return Object.entries(data.probabilities)
            .filter(([loc]) => loc !== 'not_hired')
            .map(([loc, prob]) => ({ loc, pct: Number(prob) }))
            .sort((a, b) => b.pct - a.pct);
    }, [data]);

    const eidikiAgogiLockedToYes = useMemo(
        () => EIDIKI_AGOGI_DEFAULT_YES_SPECIALTIES.includes(specialty),
        [specialty]
    );

    const resultsTitle = useMemo(
        () =>
            `Εκτίμηση πιθανότητας πρόσληψης ανά περιοχή ${formatHireDateTitleSuffix(hireMonth, hireDay)}`,
        [hireMonth, hireDay]
    );

    const filterSummary = useMemo(() => {
        const parts = [];
        if (specialty) parts.push(specialty);
        if (userPosition) parts.push(`θέση ${userPosition}`);
        if (locations.length > 0) {
            const count = locations.length;
            parts.push(`${count} ${count === 1 ? 'περιοχή' : 'περιοχές'}`);
        }
        return parts.join(' · ');
    }, [specialty, userPosition, locations]);

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
        }
    }, [token, navigate]);

    useEffect(() => {
        if (!token) return;
        const url = `${getHost()}/api/predict-usage`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(async (response) => {
                if (!response.ok) throw new Error('Αποτυχία ανάκτησης στοιχείων πρόσβασης.');
                return response.json();
            })
            .then((usage) => {
                setIsPremiumUser(Boolean(usage?.is_premium));
                if (usage?.remaining_free_attempts != null) {
                    setRemainingFreeAttempts(Number(usage.remaining_free_attempts));
                }
            })
            .catch(() => {
                // Keep page functional even if usage info fails to load.
            });
    }, [token]);

    useEffect(() => {
        setSpecialtyLoading(true);
        const url = `${getHost()}/api/specialty-types?year_range=${FIXED_YEAR_RANGE}`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        })
            .then(response => response.json())
            .then(data => setSpecialtyOptions(data.results || []))
            .catch(err => console.error('Error fetching specialties:', err))
            .finally(() => setSpecialtyLoading(false));
    }, [token]);

    useEffect(() => {
        let ignore = false;
        if (specialty) {
            setPlacesLoading(true);
            const url = `${getHost()}/api/places?year_range=${FIXED_YEAR_RANGE}&specialty=${specialty}&multi_year=true`;
            fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(response => response.json())
                .then(data => { if (!ignore) setPlacesOptions(data.results || []); })
                .catch(err => console.error('Error fetching places:', err))
                .finally(() => { if (!ignore) setPlacesLoading(false); });
        } else {
            setPlacesOptions([]);
            setLocations([]);
        }
        return () => { ignore = true; };
    }, [specialty, token]);

    useEffect(() => {
        if (hireDay > maxDaysForMonth) {
            setHireDay(maxDaysForMonth);
            return;
        }
        if (hireMonth === 9 && hireDay < 10) {
            setHireDay(10);
        }
    }, [hireMonth, maxDaysForMonth, hireDay]);

    const handleLogout = () => {
        localStorage.removeItem('authToken');
        localStorage.setItem('logout-event', Date.now());
        navigate('/', { replace: true });
    };

    const handlePositionChange = (e) => {
        const value = e.target.value.trimEnd();
        if (/^\d*$/.test(value)) {
            setUserPosition(value);
        }
    };

    const handleLocationChange = (selected) => {
        const next = selected ? selected.slice(0, MAX_LOCATIONS).map((s) => s.value) : [];
        setLocations(next);
    };

    const placeOptions = useMemo(
        () =>
            placesOptions
                .filter((p) => p !== 'Σύνολο')
                .sort((a, b) => a.localeCompare(b, 'el'))
                .map((place) => ({ value: place, label: place })),
        [placesOptions]
    );

    const locationsValue = useMemo(
        () => locations.map((loc) => ({ value: loc, label: loc })),
        [locations]
    );

    const locationSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: '42px',
            boxShadow: state.isFocused
                ? '0 0 0 2px rgba(80, 137, 162, 0.45)'
                : '0 4px 8px rgba(0, 0, 0, 0.15)',
            borderColor: state.isFocused
                ? '#5089a2'
                : 'var(--bs-border-color, #dee2e6)',
            cursor: 'text',
        }),
        valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap',
            overflowX: 'hidden',
            paddingTop: 4,
            paddingBottom: 4,
            gap: 4,
        }),
        // Hide internal chips so selections are shown in custom area below
        multiValue: (base) => ({
            ...base,
            display: 'none',
        }),
        multiValueLabel: (base) => ({
            ...base,
            display: 'none',
        }),
        multiValueRemove: (base) => ({
            ...base,
            display: 'none',
        }),
        menu: (base) => ({
            ...base,
            zIndex: 5,
        }),
        option: (base, state) => ({
            ...base,
            fontSize: '0.9rem',
            backgroundColor: state.isSelected
                ? '#5089a2'
                : state.isFocused
                    ? 'rgba(80, 137, 162, 0.12)'
                    : 'white',
            color: state.isSelected ? 'white' : 'inherit',
        }),
    };

    const handleSubmit = () => {
        setError('');
        setLoading(true);
        setSubmitted(false);
        setRatingScaleOpen(false);

        if (!tableType) {
            setError('Επιλέξτε είδος πίνακα.');
            setLoading(false);
            return;
        }

        const url = `${getHost()}/api/predict`;
        const requestBody = {
            eidiki_agogi: eidikiAgogi === 'true',
            hire_date_str: hireDateStr,
            locations,
            specialty,
            user_position: parseInt(userPosition, 10),
            table_type: tableType,
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(requestBody),
        })
            .then(async (response) => {
                const responseBody = await response.json().catch(() => ({}));
                if (!response.ok) {
                    if (response.status === 403 && responseBody?.error_code === 'PREDICT_LIMIT_REACHED') {
                        if (responseBody?.remaining_free_attempts != null) {
                            setRemainingFreeAttempts(Number(responseBody.remaining_free_attempts));
                        }
                        trackEvent('predict_limit_reached');
                        setShowPremiumModal(true);
                        return null;
                    }
                    throw new Error(responseBody?.message || 'Αποτυχία αιτήματος');
                }
                return responseBody;
            })
            .then(result => {
                if (!result) return;
                if (result?.remaining_free_attempts != null) {
                    setRemainingFreeAttempts(Number(result.remaining_free_attempts));
                }
                setData(result);
                setSubmitted(true);
                trackEvent('predict_submit', {
                    is_premium: Boolean(isPremiumUser),
                    location_count: locations.length,
                    specialty,
                });
                collapseFilterSidebarOnMobile(setSidebarOpen);
            })
            .catch(err => {
                setError(err.message || 'Σφάλμα κατά την υποβολή.');
                setData(null);
            })
            .finally(() => setLoading(false));
    };

    const isFormValid =
        specialty !== '' &&
        locations.length > 0 &&
        userPosition !== '' &&
        tableType !== '';

    const minDayForMonth = hireMonth === 9 ? 10 : 1;
    const dayOptions = Array.from(
        { length: maxDaysForMonth - minDayForMonth + 1 },
        (_, i) => minDayForMonth + i
    );
    const hasFreeAttemptsBanner = isPremiumUser === false;

    return (
        <div id="root">
            <Header handleLogout={handleLogout} />
            <main className="main-container ranking-per-state-page predict-page">
                {isPremiumUser === false && (
                    <div className="predict-free-attempts-banner px-3 pt-2">
                        <div className="alert alert-info py-2 mb-2 small" role="status">
                            Απομένουν <strong>{remainingFreeAttempts ?? 0}</strong> από τις <strong>3</strong> δωρεάν προσπάθειες
                            για το εργαλείο προβλέψεων.
                        </div>
                    </div>
                )}
                <Modal
                    show={showPremiumModal}
                    onHide={() => setShowPremiumModal(false)}
                    centered
                    enforceFocus={false}
                    restoreFocus={false}
                    mountOnEnter
                    unmountOnExit
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Premium πρόσβαση</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="mb-0">
                            Με μια μικρή χορηγία μέσω PayPal, ξεκλειδώνεις την πρόσβαση στο εργαλείο{' '}
                            <strong>Εκτιμήσεις πιθανοτήτων πρόσληψης</strong> μέχρι τις{' '}
                            <strong>{PREMIUM_ACCESS_UNTIL_LABEL}</strong>! Είναι ο δικός μας τρόπος να πούμε ένα μεγάλο
                            «ευχαριστώ» που στηρίζεις την προσπάθειά μας.
                        </p>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-center">
                        <div className="paypal-donate-modal-wrap">
                            <PayPalDonateSdkButton
                                authToken={token}
                                getHost={getHost}
                                active={showPremiumModal}
                                inModal
                                donationSource="premium_upgrade"
                                requireAuthForComplete
                                fallbackCustomFromJwt
                                onPremiumGranted={() => setIsPremiumUser(true)}
                            />
                        </div>
                    </Modal.Footer>
                </Modal>
                <div className="predict-layout-row">
                <FilterSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    filterSummary={filterSummary}
                    bodyId="predict-sidebar-body"
                    className={hasFreeAttemptsBanner ? 'predict-sidebar-with-banner' : ''}
                >
                        <Form>
                            <Form.Group className="mb-3" controlId="predict.specialty">
                                <Form.Label className="custom-label">Ειδικότητα:</Form.Label>
                                <Form.Select
                                    className="custom-form"
                                    value={specialty}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSpecialty(value);
                                        setLocations([]);
                                        setData(null);
                                        if (EIDIKI_AGOGI_DEFAULT_YES_SPECIALTIES.includes(value)) {
                                            setEidikiAgogi('true');
                                        } else {
                                            setEidikiAgogi('false');
                                            setTableType(EIDIKI_TABLE_OPTIONS[0]?.value || '');
                                        }
                                    }}
                                    disabled={specialtyLoading}
                                >
                                    <option value="">{specialtyLoading ? 'Φόρτωση...' : 'Επιλέξτε...'}</option>
                                    {!specialtyLoading && (specialtyOptions.length > 0 ? (
                                        specialtyOptions
                                            .slice()
                                            .sort((a, b) => a.localeCompare(b))
                                            .map((spec) => (
                                                <option key={spec} value={spec}>{spec}</option>
                                            ))
                                    ) : (
                                        <option disabled>Δεν υπάρχουν διαθέσιμες ειδικότητες</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="predict.eidikiAgogi">
                                <Form.Label className="custom-label">Ειδική Αγωγή:</Form.Label>
                                <Form.Select
                                    className="custom-form"
                                    value={eidikiAgogi}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setEidikiAgogi(value);
                                        setData(null);
                                        if (value === 'false') {
                                            setTableType(EIDIKI_TABLE_OPTIONS[0]?.value || '');
                                        }
                                    }}
                                    disabled={eidikiAgogiLockedToYes}
                                >
                                    <option value="false">Όχι</option>
                                    <option value="true">Ναι</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="predict.userPosition">
                                <Form.Label className="custom-label">Θέση πίνακα:</Form.Label>
                                <Form.Control
                                    className="custom-form mb-2"
                                    type="text"
                                    placeholder="Εισάγετε τη θέση σας στον πίνακα"
                                    value={userPosition}
                                    onChange={handlePositionChange}
                                />
                                <Form.Text className="text-muted fst-italic d-block mt-1">
                                    Συμπληρώστε την εκκαθαρισμένη θέση σας μετά τους τελευταίους διορισμούς.
                                </Form.Text>
                                <Form.Text className="text-muted fst-italic d-block mt-1">
                                    Εάν δεν γνωρίζετε τη θέση σας, επισκεφτείτε τη σελίδα{' '}
                                    <Link to="/ranking-personal-details" className="text-decoration-none predict-position-hint-link">
                                        Εύρεση θέσης πίνακα
                                    </Link>.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="predict.locations">
                                <Form.Label className="custom-label">
                                    Περιοχές (επιλέξτε έως {MAX_LOCATIONS}):
                                </Form.Label>
                                {specialty && (
                                    <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
                                        <Form.Text className="text-muted">
                                            {locations.length}/{MAX_LOCATIONS} επιλεγμένες
                                        </Form.Text>
                                        {locations.length > 0 && (
                                            <div className="d-inline-flex">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    type="button"
                                                    className="rounded-pill"
                                                    style={{
                                                        width: 'auto',
                                                        whiteSpace: 'nowrap',
                                                        flex: '0 0 auto',
                                                        paddingLeft: '5px',
                                                        paddingRight: '5px',
                                                    }}
                                                    onClick={() => setLocations([])}
                                                >
                                                    Καθαρισμός επιλογών
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                <Select
                                    isMulti
                                    placeholder={placesLoading ? 'Φόρτωση...' : 'Αναζήτηση και επιλογή περιοχών...'}
                                    loadingMessage={() => 'Φόρτωση...'}
                                    noOptionsMessage={() => 'Δεν βρέθηκαν περιοχές'}
                                    options={placeOptions}
                                    value={locationsValue}
                                    onChange={handleLocationChange}
                                    isDisabled={!specialty || placesLoading}
                                    isLoading={placesLoading}
                                    maxMenuHeight={280}
                                    closeMenuOnSelect={false}
                                    blurInputOnSelect={false}
                                    hideSelectedOptions={false}
                                    styles={locationSelectStyles}
                                    className="predict-location-select"
                                    classNamePrefix="predict-location"
                                />
                                {locations.length === 0 && specialty && (
                                    <Form.Text className="text-muted d-block mt-1">
                                        Δεν έχουν επιλεγεί περιοχές. Πληκτρολογήστε για να αναζητήσετε και να προσθέσετε περιοχές από τη λίστα.
                                    </Form.Text>
                                )}
                                {locations.length > 0 && (
                                    <div className="mt-2 d-flex flex-wrap gap-2">
                                        {locations.map((loc) => (
                                            <span
                                                key={loc}
                                                className="badge rounded-pill text-bg-light border px-2 py-1 d-flex align-items-center gap-1"
                                            >
                                                <span>{loc}</span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link p-0 text-muted"
                                                    onClick={() =>
                                                        setLocations((prev) =>
                                                            prev.filter((l) => l !== loc)
                                                        )
                                                    }
                                                    aria-label={`Αφαίρεση ${loc}`}
                                                >
                                                    <i className="bi bi-x" aria-hidden="true" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="predict.hireDate">
                                <Form.Label className="custom-label">Ημερομηνία φάσης προσλήψεων (10/09 – 30/06):</Form.Label>
                                <div className="predict-date-row d-flex flex-nowrap gap-2 align-items-center mb-2">
                                    <Form.Select
                                        className="custom-form predict-date-select predict-date-day"
                                        value={hireDay}
                                        onChange={(e) => {
                                            setHireDay(Number(e.target.value));
                                            setData(null);
                                        }}
                                    >
                                        {dayOptions.map((d) => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Select
                                        className="custom-form predict-date-select predict-date-month"
                                        value={hireMonth}
                                        onChange={(e) => {
                                            setHireMonth(Number(e.target.value));
                                            setData(null);
                                        }}
                                    >
                                        {SCHOOL_YEAR_MONTHS.map((m) => (
                                            <option key={m.month} value={m.month}>{m.label}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                                <Form.Text className="text-muted fst-italic d-block mt-1">
                                    Επιλέξτε μήνα και ημέρα μεταξύ 10 Σεπτεμβρίου και 30 Ιουνίου.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="predict.tableType">
                                <Form.Label className="custom-label">Είδος πίνακας:</Form.Label>
                                <Form.Select
                                    className="custom-form"
                                    value={tableType}
                                    onChange={(e) => {
                                        setTableType(e.target.value);
                                        setData(null);
                                    }}
                                    disabled={eidikiAgogi === 'false'}
                                >
                                    <option value="">Επιλέξτε...</option>
                                    {EIDIKI_TABLE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Form>
                        <div className="d-flex justify-content-center mt-2">
                            <Button
                                variant="secondary"
                                type="button"
                                className="predict-submit-button"
                                onClick={handleSubmit}
                                disabled={!isFormValid || loading}
                            >
                                {loading ? 'Φόρτωση...' : 'Υποβολή'}
                            </Button>
                        </div>
                </FilterSidebar>
                <div className={`ranking-per-state-main ${!sidebarOpen ? 'ranking-per-state-main--sidebar-collapsed' : ''}`}>
                    <Container fluid className="ranking-per-state-container predict-page-container">
                        <Row className="g-3 g-md-4">
                            <Col xs={12}>
                                <div className="ranking-per-state-content">
                                    <div className="predict-beta-notice alert alert-warning py-2 px-3 mb-3 small" role="status">
                                        <span className="app-beta-badge me-2" aria-label="Δοκιμαστική έκδοση">BETA</span>
                                        <strong>Δοκιμαστική έκδοση</strong>{' '}
                                    </div>
                                    {!submitted && !data && !loading && (
                                        <div className="text-muted small mb-3 predict-methodology-intro">
                                            <p className="mb-2">
                                                Η εφαρμογή αναλύει <strong>ιστορικά δεδομένα</strong> των τελευταίων 3 ετών
                                                (τοποθετήσεις ανά περιοχή) και τα συγκρίνει με τη{' '}
                                                <strong>θέση κατάταξής</strong> που δηλώνετε, για την ημερομηνία πρόσληψης
                                                που επιλέγετε. Μετά την υποβολή εμφανίζονται:
                                            </p>
                                            <ul className="mb-2 ps-3">
                                                <li className="mb-1">
                                                    <strong>Ποσοστό ανά περιοχή</strong> (0–100%): η «δυναμική» της θέσης σας
                                                    σε κάθε περιοχή ξεχωριστά. Τα ποσοστά{' '}
                                                    <strong>δεν αθροίζονται</strong> στο 100%.
                                                </li>
                                                <li className="mb-1">
                                                    <strong>Κλίμακα αξιολόγησης</strong> (υψηλό / μεσαίο / χαμηλό) δίπλα στο
                                                    διάγραμμα, και πίνακες ιστορικών προσλήψεων ανά έτος όπου υπάρχουν
                                                    δεδομένα.
                                                </li>
                                            </ul>
                                            <p className="mb-0">
                                                Τα αποτελέσματα είναι <strong>στατιστική εκτίμηση</strong> για
                                                ενημερωτική χρήση, όχι εγγύηση τοποθέτησης. Η{' '}
                                                <strong>σειρά προτίμησης</strong> των περιοχών δεν επηρεάζει τους
                                                υπολογισμούς.{' '}
                                                <Link to="/predict-methodology" className="text-decoration-none" target="_blank" rel="noopener noreferrer">
                                                    Αναλυτικός οδηγός μεθοδολογίας →
                                                </Link>
                                            </p>
                                        </div>
                                    )}
                                    {error && (
                                        <div className="alert alert-danger mb-3" role="alert">
                                            {error}
                                        </div>
                                    )}

                                    {loading && (
                                        <div className={`results me-3 mt-4${!sidebarOpen ? ' predict-results-snug-mobile' : ''}`}>
                                            <div className="predict-results-slot predict-results-loader" role="status" aria-live="polite">
                                                <div className="spinner-border text-primary" role="presentation">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <div>Φόρτωση αποτελεσμάτων...</div>
                                            </div>
                                        </div>
                                    )}

                                    {submitted && data && (
                                        <div className={`results me-3 mt-4${!sidebarOpen ? ' predict-results-snug-mobile' : ''}`}>
                                            <Card className="custom-card">
                                                <Card.Body>
                                                    <Row>
                                                        <Col xs={12}>
                                                            <Card.Title className="pb-2 text-center predict-results-title">{resultsTitle}</Card.Title>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col md={7} style={{ minWidth: 0 }}>
                                                            {sortedProbabilities.length > 0 && (() => {
                                                                const maxPct = Math.max(...sortedProbabilities.map((x) => x.pct), 1);
                                                                return (
                                                                    <>
                                                                        <div className="predict-bar-chart mt-2">
                                                                            {sortedProbabilities.map(({ loc, pct }) => (
                                                                                <div key={loc} className="predict-bar-row mb-2">
                                                                                    <div className="predict-bar-label text-nowrap" title={loc}>
                                                                                        {loc}
                                                                                    </div>
                                                                                    <div className="predict-bar-track flex-grow-1 mx-2">
                                                                                        <div
                                                                                            className="predict-bar-fill"
                                                                                            style={{ width: `${(pct / maxPct) * 100}%` }}
                                                                                            role="presentation"
                                                                                        />
                                                                                    </div>
                                                                                    <div className="predict-bar-pct text-nowrap fw-medium">
                                                                                        {pct.toFixed(1)}%
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </>
                                                                );
                                                            })()}
                                                        </Col>
                                                        <Col md={5}>
                                                            <div className="predict-rating-scale text-muted small">
                                                                <button
                                                                    type="button"
                                                                    className="predict-rating-scale-toggle d-md-none"
                                                                    onClick={() => setRatingScaleOpen((open) => !open)}
                                                                    aria-expanded={ratingScaleOpen}
                                                                    aria-controls="predict-rating-scale-body"
                                                                >
                                                                    <strong className="text-body">Κλίμακα Αξιολόγησης</strong>
                                                                    <i className={`bi bi-chevron-${ratingScaleOpen ? 'up' : 'down'}`} aria-hidden />
                                                                </button>
                                                                <strong className="text-body d-none d-md-block">Κλίμακα Αξιολόγησης:</strong>
                                                                <div
                                                                    id="predict-rating-scale-body"
                                                                    className={`predict-rating-scale-body${ratingScaleOpen ? ' is-open' : ''}`}
                                                                >
                                                                    <ul className="mt-2 mb-0">
                                                                        <li><strong>Υψηλό (80–100%):</strong> Ισχυρή θέση. Στο παρελθόν ήσασταν σχεδόν πάντα εντός εύρους και με σημαντικό προβάδισμα.</li>
                                                                        <li><strong>Μεσαίο (40–70%):</strong> Μέτρια θέση. Η τοποθέτηση στην περιοχή αυτή εξαρτάται από τις μεταβολές του εκάστοτε έτους.</li>
                                                                        <li><strong>Χαμηλό (0–30%):</strong> Αδύναμη θέση. Σπάνια ή ποτέ δεν καλύφθηκε η θέση σας ιστορικά στη συγκεκριμένη περιοχή.</li>
                                                                    </ul>
                                                                    <p className="mt-2 mb-0">
                                                                        <Link to="/predict-methodology" className="text-decoration-none small" target="_blank" rel="noopener noreferrer">
                                                                            Αναλυτικές εξηγήσεις →
                                                                        </Link>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    {data.details?.appointments_per_year_per_place && sortedProbabilities.length > 0 && (() => {
                                                        const yearData = data.details.appointments_per_year_per_place;
                                                        const lastPersonData = data.details.last_person_per_year_per_place || {};
                                                        const showTableTypeTable = eidikiAgogi === 'true' && !eidikiAgogiLockedToYes;
                                                        const tableColLg = showTableTypeTable ? 4 : 6;
                                                        const years = Object.keys(yearData).sort((a, b) => Number(b) - Number(a));
                                                        const locations = sortedProbabilities.map(({ loc }) => loc);
                                                        if (years.length === 0) return null;
                                                        return (
                                                            <Row className="mt-1 g-3">
                                                                <Col xs={12} lg={tableColLg}>
                                                                    <strong className="text-body small">Προσλήψεις ανά έτος και περιοχή</strong>
                                                                    <div className="table-responsive mt-1">
                                                                        <table className="table table-sm table-bordered small mt-1">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th className="text-start">Περιοχή</th>
                                                                                    {years.map((y) => (
                                                                                        <th key={y} className="text-center">{y}–{Number(y) + 1}</th>
                                                                                    ))}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {locations.map((loc) => (
                                                                                    <tr key={loc}>
                                                                                        <td className="text-start text-nowrap" title={loc}>
                                                                                            {loc.length > 20 ? `${loc.slice(0, 18)}…` : loc}
                                                                                        </td>
                                                                                        {years.map((y) => (
                                                                                            <td key={y} className="text-center">
                                                                                                {yearData[y]?.[loc] ?? '—'}
                                                                                            </td>
                                                                                        ))}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </Col>
                                                                <Col xs={12} lg={tableColLg}>
                                                                    <strong className="text-body small">Μόρια τελευταίου ανά έτος και περιοχή</strong>
                                                                    <div className="table-responsive mt-1">
                                                                        <table className="table table-sm table-bordered small mt-1">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th className="text-start">Περιοχή</th>
                                                                                    {years.map((y) => (
                                                                                        <th key={y} className="text-center">{y}–{Number(y) + 1}</th>
                                                                                    ))}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {locations.map((loc) => (
                                                                                    <tr key={loc}>
                                                                                        <td className="text-start text-nowrap" title={loc}>
                                                                                            {loc.length > 20 ? `${loc.slice(0, 18)}…` : loc}
                                                                                        </td>
                                                                                        {years.map((y) => {
                                                                                            const score = lastPersonData[y]?.[loc]?.score;
                                                                                            return (
                                                                                                <td key={y} className="text-center">
                                                                                                    {score != null ? Number(score).toFixed(2) : '—'}
                                                                                                </td>
                                                                                            );
                                                                                        })}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </Col>
                                                                {showTableTypeTable && (
                                                                    <Col xs={12} lg={tableColLg}>
                                                                        <strong className="text-body small">Πίνακας τελευταίου ανά έτος και περιοχή</strong>
                                                                        <div className="table-responsive mt-1">
                                                                            <table className="table table-sm table-bordered small mt-1">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th className="text-start">Περιοχή</th>
                                                                                        {years.map((y) => (
                                                                                            <th key={y} className="text-center">{y}–{Number(y) + 1}</th>
                                                                                        ))}
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {locations.map((loc) => (
                                                                                        <tr key={loc}>
                                                                                            <td className="text-start text-nowrap" title={loc}>
                                                                                                {loc.length > 20 ? `${loc.slice(0, 18)}…` : loc}
                                                                                            </td>
                                                                                            {years.map((y) => {
                                                                                                const tableType = lastPersonData[y]?.[loc]?.table_type;
                                                                                                return (
                                                                                                    <td key={y} className="text-center">
                                                                                                        {tableType ?? '—'}
                                                                                                    </td>
                                                                                                );
                                                                                            })}
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        );
                                                    })()}
                                                </Card.Body>
                                            </Card>
                                        </div>
                                    )}
                                    {!loading && !submitted && !data && !error && (
                                        <p className="text-muted small mt-3 mb-0">
                                            Συμπληρώστε τα φίλτρα στην αριστερή στήλη και πατήστε «Υποβολή» για να εμφανιστούν εκτιμήσεις πιθανοτήτων.
                                        </p>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default Predict;
